import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { X, Plus, Trash2, Check, Circle, Bell, ListTodo, StickyNote, Search, Loader2 } from "lucide-react";

const TABS = [
  { key: "tasks", label: "Tasks", icon: ListTodo },
  { key: "reminders", label: "Reminders", icon: Bell },
  { key: "notes", label: "Notes", icon: StickyNote },
];

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export function Workspace({ open, onClose, refreshKey }) {
  const [tab, setTab] = useState("tasks");
  const [tasks, setTasks] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [noteSearch, setNoteSearch] = useState("");

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r, n] = await Promise.all([
        api.get("/tasks"), api.get("/reminders"), api.get("/notes", { params: noteSearch ? { q: noteSearch } : {} }),
      ]);
      setTasks(t.data.tasks); setReminders(r.data.reminders); setNotes(n.data.notes);
    } catch {} finally { setLoading(false); }
  }, [noteSearch]);

  useEffect(() => { if (open) loadAll(); }, [open, refreshKey, loadAll]);

  const addItem = async () => {
    const v = input.trim();
    if (!v) return;
    setInput("");
    if (tab === "tasks") { const { data } = await api.post("/tasks", { title: v }); setTasks((x) => [data.task, ...x]); }
    else if (tab === "notes") { const { data } = await api.post("/notes", { content: v }); setNotes((x) => [data.note, ...x]); }
    else {
      // reminder: default to 1 hour from now if no explicit time typed
      const when = new Date(Date.now() + 60 * 60 * 1000).toISOString();
      const { data } = await api.post("/reminders", { title: v, remind_at: when });
      setReminders((x) => [...x, data.reminder].sort((a, b) => a.remind_at.localeCompare(b.remind_at)));
    }
  };

  const toggleTask = async (t) => {
    const { data } = await api.put(`/tasks/${t.id}`, { title: t.title, completed: !t.completed });
    setTasks((x) => x.map((i) => (i.id === t.id ? data.task : i)));
  };
  const delTask = async (id) => { await api.delete(`/tasks/${id}`); setTasks((x) => x.filter((i) => i.id !== id)); };
  const delReminder = async (id) => { await api.delete(`/reminders/${id}`); setReminders((x) => x.filter((i) => i.id !== id)); };
  const delNote = async (id) => { await api.delete(`/notes/${id}`); setNotes((x) => x.filter((i) => i.id !== id)); };

  if (!open) return null;

  const placeholder = tab === "tasks" ? "Add a task…" : tab === "reminders" ? "Add a reminder (in 1 hour)…" : "Write a note…";

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <aside data-testid="workspace-drawer" className="fixed z-50 top-0 right-0 h-full w-full sm:w-[420px] glass-panel border-l border-white/10 flex flex-col fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="label-mono">Assistant Workspace</div>
            <h2 className="font-heading text-lg font-medium mt-0.5">Your stuff, organised</h2>
          </div>
          <button data-testid="workspace-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 p-3 border-b border-white/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} data-testid={`workspace-tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`flex-1 flex items-center justify-center gap-2 text-sm py-2 rounded-full transition-all ${tab === t.key ? "text-[#000000] font-medium" : "text-slate-400 hover:text-white"}`}
                style={tab === t.key ? { background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" } : {}}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="p-4 border-b border-white/10 space-y-2">
          {tab === "notes" && (
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="workspace-note-search" value={noteSearch} onChange={(e) => setNoteSearch(e.target.value)}
                placeholder="Search notes" className="w-full rounded-xl bg-[#0D0D0D] border border-white/10 pl-9 pr-3 py-2 text-sm outline-none focus:border-mono-400/50" />
            </div>
          )}
          <div className="flex gap-2">
            <input data-testid="workspace-add-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addItem()} placeholder={placeholder}
              className="flex-1 rounded-xl bg-[#0D0D0D] border border-white/10 px-3 py-2 text-sm outline-none focus:border-mono-400/50" />
            <button data-testid="workspace-add-button" onClick={addItem}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-[#000000] shrink-0"
              style={{ background: "linear-gradient(90deg, #FFFFFF, #C8C8C8)" }}><Plus className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-4 space-y-2">
          {loading && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-mono-300" /></div>}

          {!loading && tab === "tasks" && (tasks.length === 0 ? <Empty text="No tasks yet. Ask Anna to add one." /> :
            tasks.map((t) => (
              <div key={t.id} data-testid="workspace-task-item" className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-white/5">
                <button data-testid="task-toggle" onClick={() => toggleTask(t)} className="text-mono-300">
                  {t.completed ? <Check className="w-4 h-4" /> : <Circle className="w-4 h-4 text-slate-500" />}
                </button>
                <span className={`text-sm flex-1 ${t.completed ? "line-through text-slate-500" : "text-slate-100"}`}>{t.title}</span>
                <button data-testid="task-delete" onClick={() => delTask(t.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )))}

          {!loading && tab === "reminders" && (reminders.length === 0 ? <Empty text="No reminders. Try “remind me at 7 PM to call Mom”." /> :
            reminders.map((r) => (
              <div key={r.id} data-testid="workspace-reminder-item" className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-white/5">
                <Bell className={`w-4 h-4 shrink-0 ${r.notified ? "text-slate-500" : "text-mono-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">{r.title}</div>
                  <div className="text-[11px] text-slate-500">{fmtWhen(r.remind_at)}</div>
                </div>
                <button data-testid="reminder-delete" onClick={() => delReminder(r.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )))}

          {!loading && tab === "notes" && (notes.length === 0 ? <Empty text="No notes yet. Say “make a note that…”." /> :
            notes.map((n) => (
              <div key={n.id} data-testid="workspace-note-item" className="flex items-start gap-3 rounded-xl px-3 py-2.5 bg-white/5">
                <StickyNote className="w-4 h-4 text-mono-300 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-100 flex-1 whitespace-pre-wrap">{n.content}</span>
                <button data-testid="note-delete" onClick={() => delNote(n.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )))}
        </div>
      </aside>
    </>
  );
}

const Empty = ({ text }) => <div className="text-sm text-slate-500 text-center py-8">{text}</div>;
